import { useIsAuthenticated } from "@azure/msal-react";
import { useToggle } from "@react-hookz/web";
import { loginRequest } from "config/auth";
import { useRouter } from "next/router";
import { msalInstance } from "pages/_app";
import { useEffect } from "react";
import { useCookies } from "react-cookie";

export const TOKEN_STORAGE_KEY = "accessToken";

export const TOKEN_EXPIRY_KEY = "tokenExpiry";

export const getAccessToken = () =>
  window.localStorage.getItem(TOKEN_STORAGE_KEY);

/**
 * Gets access token with automatic refresh if expired
 * @returns Promise with access token or null
 */
export const getAccessTokenWithRefresh = async (): Promise<string | null> => {
  const storedToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  const tokenExpiry = window.localStorage.getItem(TOKEN_EXPIRY_KEY);

  // Check if token exists and is not expired
  if (storedToken && tokenExpiry) {
    const expiryTime = parseInt(tokenExpiry, 10);
    const currentTime = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer before expiry

    // If token is still valid (with buffer), return it
    if (currentTime < expiryTime - bufferTime) {
      return storedToken;
    }
  }

  // Token is expired or doesn't exist, try to refresh
  try {
    let account = msalInstance.getActiveAccount();

    if (!account) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        account = msalInstance.getActiveAccount();
      }
    }

    if (!account) {
      console.warn("No active account found");
      return null;
    }

    const config = {
      ...loginRequest,
      account: account,
    };

    let response;

    try {
      response = await msalInstance.acquireTokenSilent(config);
    } catch (silentError) {
      console.error("Silent token acquisition failed:", silentError);
      return null;
    }

    if (response && response.accessToken) {
      // Calculate expiry time (default 1 hour if not provided)
      const expiresOn = response.expiresOn
        ? response.expiresOn.getTime()
        : Date.now() + 3600 * 1000;

      // Store token and expiry
      window.localStorage.setItem(TOKEN_STORAGE_KEY, response.accessToken);
      window.localStorage.setItem(TOKEN_EXPIRY_KEY, expiresOn.toString());

      return response.accessToken;
    }

    return null;
  } catch (error) {
    console.error("Error refreshing token:", error);
    // Clear invalid token data
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(TOKEN_EXPIRY_KEY);
    return null;
  }
};

export const useUpdateToken = () => {
  const isAuthenticated = useIsAuthenticated();
  const router = useRouter();
  const [isLoaded, toggleLoaded] = useToggle(false);
  const [, setCookie, removeCookie] = useCookies([TOKEN_STORAGE_KEY]);

  useEffect(() => {
    const getToken = async () => {
      let account = msalInstance.getActiveAccount();

      if (!account) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          account = msalInstance.getActiveAccount();
        }
      }

      if (account) {
        let response;

        const config = {
          ...loginRequest,
          account: account,
        };

        try {
          response = await msalInstance.acquireTokenSilent(config);
        } catch (error) {
          console.error(error);
          response = await msalInstance.acquireTokenRedirect(config);
        }

        if (response && response.accessToken) {
          // We set the token in two locations:
          // - Cookie: Server access
          // - LocalStorage: Client access
          try {
            setCookie(TOKEN_STORAGE_KEY, response.accessToken, {
              path: "/",
              maxAge: 3600 * 2,
              sameSite: "lax",
              secure: window.location.protocol === "https:",
            });

            const expiresOn = response.expiresOn
              ? response.expiresOn.getTime()
              : Date.now() + 3600 * 1000;

            window.localStorage.setItem(
              TOKEN_STORAGE_KEY,
              response.accessToken,
            );
            window.localStorage.setItem(TOKEN_EXPIRY_KEY, expiresOn.toString());
            toggleLoaded(true);
          } catch (error) {
            console.error(error);
          }
        }
      } else {
        removeCookie(TOKEN_STORAGE_KEY);
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        router.push("/");
      }
    };

    if (isAuthenticated) {
      getToken();
    }
  }, [isAuthenticated]);

  return isLoaded;
};
